import type { ReactNode } from 'react';
import type { ApprovalRequest } from '@/lib/types';
import { formatPKR } from '../format';
import { BucketShell } from './_BucketShell';
import { readStructuredFields } from './_payload';

export interface GrnDetailSplitUnsupportedViewProps {
  req: ApprovalRequest;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function readText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

function readNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function readAmount(value: unknown): string {
  return formatPKR(readNumber(value));
}

export function GrnDetailSplitUnsupportedView({
  req,
}: GrnDetailSplitUnsupportedViewProps) {
  const sf = readStructuredFields(req);
  const split = isRecord(sf['grn_detail_split']) ? sf['grn_detail_split'] : {};
  const candidates = Array.isArray(split['grn_candidates'])
    ? split['grn_candidates'].filter((item): item is Record<string, unknown> =>
        isRecord(item),
      )
    : [];

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-orange-300 bg-orange-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">
          GRN detail split unsupported
        </p>
        <div className="mt-12 grid gap-12 tablet:grid-cols-4">
          <Cell label="GRN" value={readText(split['grn_no'])} />
          <Cell label="Invoice subtotal" value={readAmount(split['invoice_subtotal'])} />
          <Cell label="GRN net total" value={readAmount(split['grn_net_total'])} />
          <Cell label="Net residual" value={readAmount(split['net_residual'])} />
        </div>
        <p className="mt-12 text-xs text-neutral-700">
          The invoice appears to split a GRN detail quantity. The automation can
          post whole GRN detail rows only, so Finance should manually review this
          split before any Oracle posting.
        </p>
        {candidates.length > 0 ? (
          <div className="mt-12 overflow-x-auto rounded-md border border-orange-200 bg-neutral-0">
            <table className="min-w-full divide-y divide-orange-100 text-sm">
              <thead className="bg-orange-100/60 text-xs font-semibold uppercase tracking-wide text-orange-900">
                <tr>
                  <Th>Detail</Th>
                  <Th>PO</Th>
                  <Th>Item</Th>
                  <Th>Qty</Th>
                  <Th>Net</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100">
                {candidates.map((candidate, index) => (
                  <tr key={`${readText(candidate['grn_det_id'])}-${index}`}>
                    <Td mono>{readText(candidate['grn_det_id'])}</Td>
                    <Td mono>{readText(candidate['po_no'])}</Td>
                    <Td>{readText(candidate['item_desc'] ?? candidate['detail_item_desc'])}</Td>
                    <Td mono>{readText(candidate['quantity'])}</Td>
                    <Td mono>{readAmount(candidate['grn_net_amount'] ?? candidate['amount'])}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </BucketShell>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-orange-900">
        {label}
      </p>
      <p className="mt-4 font-mono text-sm font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-12 py-8 text-left">{children}</th>;
}

function Td({
  children,
  mono = false,
}: {
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <td className={`px-12 py-8 align-top ${mono ? 'font-mono text-xs' : ''}`}>
      {children}
    </td>
  );
}
